import React, { useState } from 'react';
import SignaturePad from '../../engagement/SignaturePad';
import type { JobOfferFields, JobOfferSection, JobOfferSignature } from '../../../types/hiring';
import {
  employeeFullName,
  formatOfferDate,
  formatOfferPay,
  LCTF_OFFER_COMPANY,
} from '../../../utils/jobOfferLetter';
import JobOfferPreview from './JobOfferPreview';
import '../../../pages/PublicProposalPage.css';
import '../../../pages/PublicJobOfferPage.css';

export const LCTF_WORDMARK_SRC = '/assets/logo/lctf-wordmark.png';

export type OfferPortalNavStep = 'offer' | 'contract' | 'policies' | 'start';

export type OfferPortalPolicy = {
  id: string;
  title: string;
  policyNumber?: string;
  summary?: string;
  body: string;
  bodyFormat?: 'html' | 'plain';
};

export function OfferPortalNav({
  current,
  onSelect,
  contractReleased,
  policiesUnlocked,
  startUnlocked,
  preview,
}: {
  current: OfferPortalNavStep;
  onSelect?: (step: OfferPortalNavStep) => void;
  offerSigned?: boolean;
  contractReleased?: boolean;
  policiesUnlocked?: boolean;
  startUnlocked?: boolean;
  preview?: boolean;
}) {
  const go = (step: OfferPortalNavStep) => () => onSelect?.(step);
  const contractOpen = Boolean(preview || contractReleased);
  const policiesOpen = Boolean(preview || policiesUnlocked);
  const startOpen = Boolean(preview || startUnlocked);
  return (
    <nav className="offer-portal-steps" aria-label="Offer steps">
      <button type="button" className={current === 'offer' ? 'is-current' : ''} onClick={go('offer')}>
        Offer
      </button>
      <button
        type="button"
        className={current === 'contract' ? 'is-current' : !contractOpen ? 'is-later' : ''}
        disabled={!contractOpen}
        onClick={go('contract')}
      >
        Contract
      </button>
      <button
        type="button"
        className={current === 'policies' ? 'is-current' : !policiesOpen ? 'is-later' : ''}
        disabled={!policiesOpen}
        onClick={go('policies')}
      >
        Policies
      </button>
      <button
        type="button"
        className={current === 'start' ? 'is-current' : !startOpen ? 'is-later' : ''}
        disabled={!startOpen}
        onClick={go('start')}
      >
        Start date
      </button>
    </nav>
  );
}

type Props = {
  fields: JobOfferFields;
  sections: JobOfferSection[];
  ceoSignature?: JobOfferSignature | null;
  employeeSignature?: JobOfferSignature | null;
  logoUrl?: string | null;
  backgroundUrl?: string | null;
  preview?: boolean;
  documentKind?: 'offer' | 'contract';
  navStep?: OfferPortalNavStep;
  onNavStep?: (step: OfferPortalNavStep) => void;
  offerSigned?: boolean;
  contractSigned?: boolean;
  contractReleased?: boolean;
  policiesUnlocked?: boolean;
  startUnlocked?: boolean;
  policies?: OfferPortalPolicy[];
  startConfirmed?: boolean;
  onSign?: (sig: { dataUrl: string; signedBy: string }) => void;
  signDisabled?: boolean;
};

export function PublicJobOfferView({
  fields,
  sections,
  ceoSignature,
  employeeSignature,
  logoUrl,
  backgroundUrl,
  preview,
  documentKind = 'offer',
  navStep,
  onNavStep,
  offerSigned,
  contractSigned,
  contractReleased,
  policiesUnlocked,
  startUnlocked,
  policies = [],
  startConfirmed,
  onSign,
  signDisabled,
}: Props) {
  const name = employeeFullName(fields);
  const signed = Boolean(employeeSignature?.dataUrl);
  const src = logoUrl || LCTF_WORDMARK_SRC;
  const isContract = documentKind === 'contract';
  const current = navStep || (isContract ? 'contract' : 'offer');
  const letterDone = Boolean(offerSigned) || (!isContract && signed);
  const agreementDone = Boolean(contractSigned);
  const canSignContract = letterDone && Boolean(contractReleased) && !signed;
  const [openPolicyId, setOpenPolicyId] = useState<string | null>(null);

  let signBlock: React.ReactNode;
  if (signed && !isContract) {
    signBlock = (
      <div className="offer-portal-sign">
        <h3>Offer letter signed</h3>
        {contractReleased || preview ? (
          <>
            <p>Next, open the Contract tab and sign the employment agreement.</p>
            <button type="button" className="offer-portal-primary" onClick={() => onNavStep?.('contract')}>
              Continue to the employment agreement
            </button>
          </>
        ) : (
          <p>
            Next, {LCTF_OFFER_COMPANY} will send the employment agreement. Watch for an email, then open the
            Contract tab to sign it.
          </p>
        )}
      </div>
    );
  } else if (signed && isContract) {
    signBlock = (
      <div className="offer-portal-sign">
        <h3>Employment agreement signed</h3>
        <p>Next, review and acknowledge the handbook policies.</p>
        <button type="button" className="offer-portal-primary" onClick={() => onNavStep?.('policies')}>
          Continue to policies
        </button>
      </div>
    );
  } else if (isContract && !contractReleased && !preview) {
    signBlock = (
      <p className="offer-portal-locked">
        The employment agreement will be available after {LCTF_OFFER_COMPANY} sends it. You will get an email
        when it is ready. Then click the Contract tab to sign.
      </p>
    );
  } else if (isContract && !letterDone) {
    signBlock = (
      <div className="offer-portal-sign">
        <h3>Sign the offer letter first</h3>
        <p>Accept the offer on the Offer tab. After that, you can sign this employment agreement.</p>
        <button type="button" className="offer-portal-primary" onClick={() => onNavStep?.('offer')}>
          Back to the offer letter
        </button>
      </div>
    );
  } else if (preview) {
    signBlock = (
      <div className="offer-portal-sign">
        <h3>Candidate signature</h3>
        <p>
          {isContract
            ? 'After they sign the offer letter, they continue to this tab and sign the contract.'
            : <>The applicant will type <strong>{name || 'their name'}</strong>, sign, then continue to the Contract tab.</>}
        </p>
      </div>
    );
  } else if (isContract && !canSignContract) {
    signBlock = (
      <p className="offer-portal-locked">
        Sign the offer letter first. Then return to the Contract tab to sign the employment agreement.
      </p>
    );
  } else {
    signBlock = (
      <div className="offer-portal-sign">
        <h3>{isContract ? 'Sign the employment agreement' : 'Sign the offer letter'}</h3>
        <p>Type your name exactly as <strong>{name}</strong> and sign below.</p>
        <SignaturePad initialName={name} onSign={sig => onSign?.(sig)} disabled={signDisabled} />
      </div>
    );
  }

  const showingPolicies = current === 'policies';
  const showingStart = current === 'start';

  return (
    <div className={`proposal-public-page offer-public-page${preview ? ' is-preview' : ''}`}>
      {backgroundUrl && (
        <div className="proposal-background" style={{ backgroundImage: `url(${backgroundUrl})` }} />
      )}
      <div className="proposal-background-overlay" />
      <div className="proposal-container">
        {preview && (
          <p className="offer-preview-banner" role="status">Preview of the candidate page. They cannot sign from here.</p>
        )}
        <header className="proposal-header-module">
          <div className="header-logo">
            <img src={src} alt={LCTF_OFFER_COMPANY} className="proposal-logo" />
          </div>
          <div className="header-divider" />
          <div className="header-message">
            <p className="thank-you-message">
              {showingPolicies
                ? <>Review the employee handbook policies for <strong>{LCTF_OFFER_COMPANY}</strong>.</>
                : showingStart
                  ? <>Confirm your start date with <strong>{LCTF_OFFER_COMPANY}</strong>.</>
                  : isContract
                    ? <>Please review and sign the <strong>Employment Agreement</strong> with {LCTF_OFFER_COMPANY}.</>
                    : <>We are pleased to offer you a place on the <strong>{LCTF_OFFER_COMPANY}</strong> team.</>}
            </p>
            <p className="owner-signature">— Matt Lynch, CEO</p>
          </div>
        </header>
        <OfferPortalNav
          current={current}
          onSelect={onNavStep}
          offerSigned={letterDone}
          contractReleased={contractReleased}
          policiesUnlocked={policiesUnlocked || agreementDone}
          startUnlocked={startUnlocked}
          preview={preview}
        />
        <p className="offer-portal-howto">
          Complete these steps in order: sign the offer letter, sign the employment agreement, review policies, then confirm your start date.
        </p>
        <section className="offer-portal-panel">
          {showingPolicies ? (
            <div className="offer-paper">
              <h2>Employee handbook policies</h2>
              <p>
                {preview
                  ? 'Candidates open and acknowledge each required policy here after the agreement is signed.'
                  : 'Open and acknowledge each required policy to continue.'}
              </p>
              {policies.length === 0 && <p>No published policies need review.</p>}
              <ul className="offer-policy-list">
                {policies.map(policy => {
                  const open = openPolicyId === policy.id;
                  return (
                    <li key={policy.id}>
                      <button type="button" onClick={() => setOpenPolicyId(open ? null : policy.id)}>
                        {policy.policyNumber ? `${policy.policyNumber} — ` : ''}{policy.title}
                      </button>
                      {open && (
                        <div className="offer-policy-body">
                          {policy.summary ? <p className="offer-policy-summary">{policy.summary}</p> : null}
                          {policy.bodyFormat === 'html' ? (
                            <div dangerouslySetInnerHTML={{ __html: policy.body }} />
                          ) : (
                            <p style={{ whiteSpace: 'pre-wrap' }}>{policy.body}</p>
                          )}
                          {preview ? (
                            <p className="offer-portal-locked">Preview only. The candidate acknowledges each policy here.</p>
                          ) : null}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <button type="button" className="offer-portal-primary" onClick={() => onNavStep?.('start')}>
                Continue to start date
              </button>
            </div>
          ) : showingStart ? (
            <div className="offer-paper">
              <h2>{startConfirmed ? "You're hired" : 'Confirm your start date'}</h2>
              <p>
                Your start date is <strong>{formatOfferDate(fields.startDate)}</strong>
                {fields.positionTitle ? ` for ${fields.positionTitle}` : ''}.
              </p>
              {preview ? (
                <p>Preview only. The candidate confirms they can begin on this date.</p>
              ) : startConfirmed ? (
                <p>
                  Welcome to {LCTF_OFFER_COMPANY}. We will be in touch with first-day details.
                </p>
              ) : (
                <p>Confirm that you can begin on this date. The shop will follow up with onboarding details.</p>
              )}
            </div>
          ) : (
            <>
              <div className="offer-summary">
                <p><strong>Position</strong> {fields.positionTitle}</p>
                <p><strong>Start date</strong> {formatOfferDate(fields.startDate)}</p>
                <p><strong>Pay</strong> {formatOfferPay(fields)}</p>
                {fields.location ? <p><strong>Location</strong> {fields.location}</p> : null}
              </div>
              <JobOfferPreview
                sections={sections}
                employeeName={name}
                ceoName={fields.ceoName}
                ceoTitle={fields.ceoTitle}
                ceoSignature={ceoSignature}
                employeeSignature={employeeSignature}
                documentTitle={isContract ? 'Employment Agreement' : 'Offer Letter'}
                companySignName={isContract ? 'LC TIMBER FRAMES' : undefined}
              />
              {signBlock}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default PublicJobOfferView;
